import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { eventAPI, attendeeAPI, sponsorAPI, specialGuestAPI, eoiAPI, feedbackAPI, resolveAssetUrl, exportAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import RsvpReconcilePanel from '../components/RsvpReconcilePanel';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  Users,
  QrCode,
  Copy,
  Edit,
  Download,
  Mail,
  CheckCircle,
  LogOut as CheckOut,
  Loader2,
  Search,
  ExternalLink,
  Target,
  User,
  BookOpen,
  FileText,
  History,
  Package,
  Send,
  Building2,
  FileSignature,
  Award,
  Pencil,
  X,
  Save,
  CalendarPlus,
  Trash2,
  AlertTriangle,
  FileSpreadsheet,
  FileDown,
  Bell,
  Heart
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { PagerBar, usePagedList } from '../components/PagerBar';
import QRCode from 'react-qr-code';

export default function EventDetail() {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingCert, setSendingCert] = useState(null);
  const [checkingIn, setCheckingIn] = useState(null);
  const [customEmailDialog, setCustomEmailDialog] = useState({ open: false, attendeeId: null, attendeeName: '' });
  const [customEmail, setCustomEmail] = useState('');
  const [sendingCustom, setSendingCustom] = useState(false);
  const [downloadingBulk, setDownloadingBulk] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingEvent, setDeletingEvent] = useState(false);
  const [invitationStatusFilter, setInvitationStatusFilter] = useState('all');
  const [invitationDialog, setInvitationDialog] = useState({ open: false, mode: 'create', invitation: null });
  const [invitationForm, setInvitationForm] = useState({
    guest_name: '',
    guest_title: '',
    guest_email: '',
    role: 'Special Guest',
    custom_message: '',
  });
  const [savingInvitation, setSavingInvitation] = useState(false);
  const [downloadingInvitation, setDownloadingInvitation] = useState(null);
  const [sendingInvitation, setSendingInvitation] = useState(null);
  const [sendInvitationDialog, setSendInvitationDialog] = useState({ open: false, invitation: null });
  const [sendForm, setSendForm] = useState({ email: '', cover_message: '' });
  const [sendError, setSendError] = useState('');
  // Sponsorship EOI state
  const [eois, setEois] = useState([]);
  const [eoiActionId, setEoiActionId] = useState(null);
  const [emailingEoiId, setEmailingEoiId] = useState(null);
  // Bulk thank-you (post-event sponsor thank-you blast)
  const [thankSponsorsConfirmOpen, setThankSponsorsConfirmOpen] = useState(false);
  const [thankSponsorsSending, setThankSponsorsSending] = useState(false);
  const [thankSponsorsResult, setThankSponsorsResult] = useState(null);
  // Attendees tab: search + filter + pagination
  const [attendeesSearchQ, setAttendeesSearchQ] = useState('');
  const [attendeesFilter, setAttendeesFilter] = useState('all'); // all | checked_in | not_checked_in
  // Bulk reminder email
  const [reminderConfirmOpen, setReminderConfirmOpen] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [pushCertsConfirmOpen, setPushCertsConfirmOpen] = useState(false);
  const [pushingCerts, setPushingCerts] = useState(false);
  const [pushCertsResult, setPushCertsResult] = useState(null);
  // Feedback tab: bulk-email dialog
  const [feedbackBulkDialog, setFeedbackBulkDialog] = useState(false);
  const [feedbackBulkCover, setFeedbackBulkCover] = useState('');
  const [feedbackBulkSending, setFeedbackBulkSending] = useState(false);
  const [feedbackBulkResult, setFeedbackBulkResult] = useState(null);

  useEffect(() => {
    fetchData();
  }, [eventId]);

  const fetchData = async () => {
    try {
      const [eventRes, attendeesRes] = await Promise.all([
        eventAPI.get(eventId),
        attendeeAPI.list(eventId)
      ]);
      setEvent(eventRes.data);
      setAttendees(attendeesRes.data);
      
      // Fetch sponsors for this event
      try {
        const sponsorsRes = await sponsorAPI.getEventSponsors(eventId);
        setSponsors(sponsorsRes.data);
      } catch (e) {
        console.log('No sponsors');
      }
      
      // Fetch audit logs
      try {
        const auditRes = await eventAPI.getAudit(eventId);
        setAuditLogs(auditRes.data);
      } catch (e) {
        console.log('No audit logs');
      }

      // Fetch special-guest invitations
      try {
        const invRes = await specialGuestAPI.list(eventId);
        setInvitations(invRes.data);
      } catch (e) {
        console.log('Could not load invitations');
      }

      // Fetch sponsorship EOIs (public form submissions)
      try {
        const eoiRes = await eoiAPI.list(eventId);
        setEois(eoiRes.data);
      } catch (e) {
        console.log('Could not load EOIs');
      }
    } catch (error) {
      toast.error('Failed to load event');
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  const openInvitationDialog = (mode, invitation = null) => {
    if (mode === 'create') {
      setInvitationForm({
        guest_name: '',
        guest_title: '',
        guest_email: '',
        role: 'Special Guest',
        custom_message: '',
      });
    } else {
      setInvitationForm({
        guest_name: invitation.guest_name || '',
        guest_title: invitation.guest_title || '',
        guest_email: invitation.guest_email || '',
        role: invitation.role || 'Special Guest',
        custom_message: invitation.custom_message || '',
      });
    }
    setInvitationDialog({ open: true, mode, invitation });
  };

  const saveInvitation = async () => {
    if (!invitationForm.guest_name?.trim()) {
      toast.error("Please enter the guest's name");
      return;
    }
    if (!invitationForm.role?.trim()) {
      toast.error('Please choose a role');
      return;
    }
    setSavingInvitation(true);
    try {
      const payload = {
        guest_name: invitationForm.guest_name.trim(),
        guest_title: (invitationForm.guest_title || '').trim(),
        guest_email: (invitationForm.guest_email || '').trim(),
        role: invitationForm.role.trim(),
        custom_message: (invitationForm.custom_message || '').trim(),
      };
      if (invitationDialog.mode === 'create') {
        await specialGuestAPI.create(eventId, payload);
        toast.success('Guest invitation drafted');
      } else {
        await specialGuestAPI.update(invitationDialog.invitation.id, payload);
        toast.success('Invitation updated');
      }
      setInvitationDialog({ open: false, mode: 'create', invitation: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save invitation');
    } finally {
      setSavingInvitation(false);
    }
  };

  const handleDownloadInvitation = async (invitation) => {
    setDownloadingInvitation(invitation.id);
    try {
      const response = await specialGuestAPI.downloadPdf(invitation.id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const safeName = (invitation.guest_name || 'guest').replace(/\s+/g, '_');
      link.setAttribute('download', `invitation_${safeName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Invitation downloaded');
    } catch (error) {
      toast.error('Failed to download invitation');
    } finally {
      setDownloadingInvitation(null);
    }
  };

  const handleDeleteInvitation = async (invitation) => {
    if (!window.confirm(`Delete the invitation for ${invitation.guest_name}?`)) return;
    try {
      await specialGuestAPI.delete(invitation.id);
      toast.success('Invitation deleted');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const openSendInvitationDialog = (invitation) => {
    setSendForm({
      email: invitation.guest_email || '',
      cover_message: '',
    });
    setSendError('');
    setSendInvitationDialog({ open: true, invitation });
  };

  const handleSendInvitation = async () => {
    if (!sendForm.email || !sendForm.email.includes('@')) {
      toast.error('Please provide a valid email address');
      return;
    }
    setSendingInvitation(sendInvitationDialog.invitation.id);
    setSendError('');
    try {
      await specialGuestAPI.sendEmail(sendInvitationDialog.invitation.id, {
        email: sendForm.email,
        cover_message: sendForm.cover_message,
      });
      toast.success(`Invitation sent to ${sendForm.email}`);
      setSendInvitationDialog({ open: false, invitation: null });
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to send invitation';
      setSendError(detail);
      toast.error(detail);
    } finally {
      setSendingInvitation(null);
    }
  };

  const setInvitationStatus = async (invitation, status) => {
    try {
      await specialGuestAPI.update(invitation.id, { status });
      fetchData();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  // === Sponsorship EOI handlers ===
  const copyPublicSponsorLink = () => {
    if (!event?.invite_code) return;
    const link = `${window.location.origin}/sponsor/${event.invite_code}`;
    navigator.clipboard.writeText(link).then(
      () => toast.success('Public sponsor link copied to clipboard'),
      () => toast.error('Could not copy link'),
    );
  };

  const handleEoiApprove = async (eoi) => {
    setEoiActionId(eoi.id);
    try {
      await eoiAPI.approve(eoi.id);
      toast.success(`Approved ${eoi.sponsor_org} — sponsor added to event`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    } finally {
      setEoiActionId(null);
    }
  };

  const handleEoiReject = async (eoi) => {
    if (!window.confirm(`Reject EOI from ${eoi.sponsor_org}?`)) return;
    setEoiActionId(eoi.id);
    try {
      await eoiAPI.update(eoi.id, { status: 'rejected' });
      toast.success('EOI rejected');
      fetchData();
    } catch (e) {
      toast.error('Failed to reject');
    } finally {
      setEoiActionId(null);
    }
  };

  const handleEoiPaymentReceived = async (eoi) => {
    setEoiActionId(eoi.id);
    try {
      await eoiAPI.paymentReceived(eoi.id);
      toast.success(`Payment recorded for ${eoi.sponsor_org}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record payment');
    } finally {
      setEoiActionId(null);
    }
  };

  const handleEoiRevertPayment = async (eoi) => {
    if (!window.confirm(`Revert payment status for ${eoi.sponsor_org}?`)) return;
    setEoiActionId(eoi.id);
    try {
      await eoiAPI.revertPayment(eoi.id);
      toast.success('Payment reverted');
      fetchData();
    } catch (e) {
      toast.error('Failed to revert');
    } finally {
      setEoiActionId(null);
    }
  };

  const handleEoiDelete = async (eoi) => {
    if (!window.confirm(`Permanently delete EOI from ${eoi.sponsor_org}?`)) return;
    setEoiActionId(eoi.id);
    try {
      await eoiAPI.delete(eoi.id);
      toast.success('EOI deleted');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete');
    } finally {
      setEoiActionId(null);
    }
  };

  const handleEoiDownloadInvoice = async (eoi) => {
    try {
      const res = await eoiAPI.downloadInvoice(eoi.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const safe = (eoi.sponsor_org || 'sponsor').replace(/\s+/g, '_');
      link.setAttribute('download', `tax_invoice_${safe}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded');
      fetchData();
    } catch (e) {
      toast.error('Failed to download invoice');
    }
  };

  const handleEoiDownloadLetter = async (eoi) => {
    try {
      const res = await eoiAPI.downloadLetter(eoi.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const safe = (eoi.sponsor_org || 'sponsor').replace(/\s+/g, '_');
      link.setAttribute('download', `sponsorship_invitation_letter_${safe}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Invitation letter downloaded');
    } catch (e) {
      toast.error('Failed to download invitation letter');
    }
  };

  const handleEoiEmailInvoice = async (eoi) => {
    setEmailingEoiId(eoi.id);
    try {
      await eoiAPI.emailInvoice(eoi.id);
      toast.success(`Invoice emailed to ${eoi.contact_email}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to email invoice');
    } finally {
      setEmailingEoiId(null);
    }
  };

  const handleThankAllSponsors = async () => {
    setThankSponsorsSending(true);
    setThankSponsorsResult(null);
    try {
      const res = await eoiAPI.thankAllSponsors(eventId);
      const { attempted, sent, skipped, failed } = res.data;
      setThankSponsorsResult(res.data);
      if (attempted === 0) {
        toast.info('No approved sponsors on this event yet');
      } else if (sent === attempted) {
        toast.success(`Thank-you sent to ${sent} sponsor${sent === 1 ? '' : 's'}`);
      } else {
        toast.warning(`Sent ${sent}/${attempted}. ${skipped} skipped, ${failed} failed — see details.`);
      }
      // Keep the dialog open when there's a failure to show details
      if (!failed && !skipped) setThankSponsorsConfirmOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send thank-you emails');
    } finally {
      setThankSponsorsSending(false);
    }
  };

  // === Feedback link handlers ===
  const feedbackPublicLink = event?.invite_code
    ? `${window.location.origin}/feedback/event/${event.invite_code}`
    : '';

  const copyFeedbackLink = () => {
    if (!feedbackPublicLink) return;
    navigator.clipboard.writeText(feedbackPublicLink).then(
      () => toast.success('Anonymous feedback link copied'),
      () => toast.error('Could not copy link'),
    );
  };

  const handleBulkEmailFeedback = async () => {
    setFeedbackBulkSending(true);
    setFeedbackBulkResult(null);
    try {
      const res = await feedbackAPI.bulkEmail(eventId, { cover_message: feedbackBulkCover });
      const { sent, total_recipients, failed } = res.data;
      setFeedbackBulkResult(res.data);
      if (sent === total_recipients) {
        toast.success(`Feedback link emailed to ${sent} checked-in attendee${sent === 1 ? '' : 's'}`);
      } else {
        toast.warning(`Sent ${sent}/${total_recipients}. ${(failed || []).length} failed — see details.`);
      }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send feedback emails');
    } finally {
      setFeedbackBulkSending(false);
    }
  };

  const handleDownloadSponsorCertificate = async (sponsorId, sponsorName) => {
    try {
      const response = await sponsorAPI.downloadCertificate(sponsorId, eventId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sponsor_certificate_${sponsorName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Sponsor certificate downloaded');
    } catch (error) {
      toast.error('Failed to download sponsor certificate');
    }
  };

  const copyInviteLink = () => {
    const link = `${window.location.origin}/register/${event.invite_code}`;
    navigator.clipboard.writeText(link);
    toast.success('Invite link copied to clipboard!');
  };

  const handleCheckIn = async (attendeeId) => {
    setCheckingIn(attendeeId);
    try {
      await attendeeAPI.checkIn(attendeeId, {});
      toast.success('Attendee checked in!');
      fetchData();
    } catch (error) {
      toast.error('Failed to check in');
    } finally {
      setCheckingIn(null);
    }
  };

  const handleCheckOut = async (attendeeId) => {
    setCheckingIn(attendeeId);
    try {
      await attendeeAPI.checkOut(attendeeId);
      toast.success('Departure time recorded');
      fetchData();
    } catch (error) {
      toast.error('Failed to record departure');
    } finally {
      setCheckingIn(null);
    }
  };

  const handleSendCertificate = async (attendeeId) => {
    setSendingCert(attendeeId);
    try {
      await attendeeAPI.sendCertificate(attendeeId);
      toast.success('Certificate sent!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send certificate');
    } finally {
      setSendingCert(null);
    }
  };

  const handleDownloadCertificate = async (attendeeId, attendeeName) => {
    try {
      const response = await attendeeAPI.downloadCertificate(attendeeId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificate_${attendeeName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Certificate downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download certificate');
    }
  };

  const handleBulkDownload = async () => {
    setDownloadingBulk(true);
    try {
      const response = await attendeeAPI.bulkDownloadCertificates(eventId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificates_${event.title.replace(/\s+/g, '_').substring(0, 30)}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('All certificates downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download certificates');
    } finally {
      setDownloadingBulk(false);
    }
  };

  const openCustomEmailDialog = (attendeeId, attendeeName, attendeeEmail) => {
    setCustomEmailDialog({ open: true, attendeeId, attendeeName });
    setCustomEmail(attendeeEmail);
  };

  const handleSendCustomEmail = async () => {
    if (!customEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    setSendingCustom(true);
    try {
      await attendeeAPI.sendCertificateCustom(customEmailDialog.attendeeId, customEmail);
      toast.success(`Certificate sent to ${customEmail}`);
      setCustomEmailDialog({ open: false, attendeeId: null, attendeeName: '' });
      setCustomEmail('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send certificate');
    } finally {
      setSendingCustom(false);
    }
  };

  const registrationUrl = event ? `${window.location.origin}/register/${event.invite_code}` : '';

  // Attendees filter + pagination (memoized matcher) — must be called unconditionally
  // BEFORE any early-return to satisfy react-hooks/rules-of-hooks
  const attendeesMatcher = (a) => {
    const q = attendeesSearchQ.trim().toLowerCase();
    const matchesText =
      !q ||
      (a.name || '').toLowerCase().includes(q) ||
      (a.email || '').toLowerCase().includes(q) ||
      (a.phone || '').toLowerCase().includes(q);
    const matchesFilter =
      attendeesFilter === 'all' ||
      (attendeesFilter === 'checked_in' && a.checked_in) ||
      (attendeesFilter === 'not_checked_in' && !a.checked_in);
    return matchesText && matchesFilter;
  };
  const attendeesPaged = usePagedList(attendees, attendeesMatcher, 20);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (!event) return null;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-start gap-4 mb-8">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/events')}
            className="mt-1"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground mb-2" data-testid="event-title">
                  {event.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {format(parseISO(event.date), 'MMMM d, yyyy')}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {event.time} • {event.duration_hours}h
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {event.venue}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={async () => {
                    try {
                      await exportAPI.sponsorshipProspectusPdf(eventId, event.title);
                      toast.success('Sponsorship prospectus downloaded');
                    } catch (e) {
                      toast.error('Failed to generate prospectus');
                    }
                  }}
                  data-testid="prospectus-header-btn"
                >
                  <FileDown className="w-4 h-4" />
                  Prospectus
                </Button>
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={async () => {
                    try {
                      await exportAPI.adminEventIcs(eventId, event.title);
                      toast.success('Calendar file downloaded');
                    } catch (e) {
                      toast.error('Failed to download .ics');
                    }
                  }}
                  data-testid="event-ics-btn"
                >
                  <CalendarPlus className="w-4 h-4" />
                  .ics
                </Button>
                <Link to={`/events/${eventId}/edit`}>
                  <Button variant="outline" className="gap-2" data-testid="edit-event-btn">
                    <Edit className="w-4 h-4" />
                    Edit
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  className="gap-2 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => setDeleteDialogOpen(true)}
                  data-testid="delete-event-detail-btn"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="grid w-full grid-cols-6">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="attendees">Attendees ({attendees.length})</TabsTrigger>
                <TabsTrigger value="sponsors">Sponsors ({sponsors.length})</TabsTrigger>
                <TabsTrigger value="invitations" data-testid="invitations-tab">Invitations ({invitations.length})</TabsTrigger>
                <TabsTrigger value="feedback">Feedback</TabsTrigger>
                <TabsTrigger value="audit">Audit</TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="space-y-6 mt-6">
                {/* Speakers */}
                {event.speakers?.length > 0 && (
                  <Card className="border-border">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <User className="w-5 h-5" />
                        Speakers
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {event.speakers.map((speaker, index) => (
                          <div key={index} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <User className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">{speaker.name}</p>
                              <p className="text-sm text-muted-foreground">
                                {[speaker.title, speaker.organization].filter(Boolean).join(', ')}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Topics */}
                {event.topics?.length > 0 && event.topics.some(t => t.trim()) && (
                  <Card className="border-border">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <BookOpen className="w-5 h-5" />
                        Topics Discussed
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {event.topics.filter(t => t.trim()).map((topic, index) => (
                          <li key={index} className="flex gap-3 text-sm">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">
                              {index + 1}
                            </span>
                            <span className="text-foreground">{topic}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Learning Objectives */}
                {event.learning_objectives?.length > 0 && event.learning_objectives.some(o => o.trim()) && (
                  <Card className="border-border">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Target className="w-5 h-5" />
                        Learning Objectives
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-4">
                        By the end of this session, participants will be able to:
                      </p>
                      <ul className="space-y-3">
                        {event.learning_objectives.filter(o => o.trim()).map((obj, index) => (
                          <li key={index} className="flex gap-3 text-sm">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">
                              {index + 1}
                            </span>
                            <span className="text-foreground">{obj}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Educational Statement */}
                {event.educational_statement && (
                  <Card className="border-border">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Educational Activity Statement
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground italic">
                        {event.educational_statement}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="attendees" className="mt-6">
                <div className="space-y-4">
                  <RsvpReconcilePanel eventId={eventId} attendees={attendees} onWalkInAdded={fetchData} />
                  <Card className="border-border">
                  <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Users className="w-5 h-5" />
                        Registered Attendees
                      </CardTitle>
                      <CardDescription>
                        {attendees.filter(a => a.checked_in).length} of {attendees.length} checked in
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      {attendees.filter(a => a.checked_in).length > 0 && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="gap-2"
                          onClick={handleBulkDownload}
                          disabled={downloadingBulk}
                          data-testid="bulk-download-btn"
                        >
                          {downloadingBulk ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Package className="w-4 h-4" />
                          )}
                          Download All Certificates
                        </Button>
                      )}
                      {attendees.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2"
                          onClick={async () => {
                            try {
                              await exportAPI.attendeesCsv(eventId, event.title);
                              toast.success('Attendees exported');
                            } catch (e) {
                              toast.error('Failed to export attendees');
                            }
                          }}
                          data-testid="export-attendees-csv-btn"
                        >
                          <FileSpreadsheet className="w-4 h-4" />
                          Export CSV
                        </Button>
                      )}
                      {attendees.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2"
                          onClick={() => setReminderConfirmOpen(true)}
                          disabled={sendingReminders}
                          data-testid="send-reminders-btn"
                          title="Email a reminder + personal check-in QR to every registered attendee"
                        >
                          {sendingReminders ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Mail className="w-4 h-4" />
                          )}
                          {sendingReminders ? 'Sending…' : 'Send Reminder to All'}
                        </Button>
                      )}
                      {attendees.filter((a) => a.checked_in).length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2"
                          onClick={() => setPushCertsConfirmOpen(true)}
                          disabled={pushingCerts}
                          data-testid="push-certs-btn"
                          title="Send CPD certificate + feedback link to every checked-in attendee who hasn't received it yet"
                        >
                          {pushingCerts ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Award className="w-4 h-4" />
                          )}
                          {pushingCerts ? 'Pushing…' : 'Push certificates'}
                        </Button>
                      )}
                      <Link to={`/checkin?event=${eventId}`}>
                        <Button variant="outline" size="sm" className="gap-2">
                          <QrCode className="w-4 h-4" />
                          Check-in Mode
                        </Button>
                      </Link>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {attendees.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No registrations yet</p>
                        <p className="text-sm mt-1">Share the invite link to get registrations</p>
                      </div>
                    ) : (
                      <>
                        {/* Catering counts strip */}
                        {(() => {
                          const veg = attendees.filter((a) => a.diet_type === 'veg').length;
                          const nonVeg = attendees.filter((a) => a.diet_type === 'non_veg').length;
                          const withNotes = attendees.filter((a) => (a.diet_notes || '').trim()).length;
                          const unspecified = attendees.filter((a) => !a.diet_type).length;
                          return (
                            <div
                              className="mb-4 flex flex-wrap items-center gap-2 px-3 py-2.5 rounded-lg border border-border bg-muted/30 text-sm"
                              data-testid="catering-summary"
                            >
                              <span className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                                Catering
                              </span>
                              <Badge variant="secondary" className="bg-green-100 text-green-700" data-testid="catering-veg-count">
                                Veg: {veg}
                              </Badge>
                              <Badge variant="secondary" className="bg-orange-100 text-orange-700" data-testid="catering-nonveg-count">
                                Non-veg: {nonVeg}
                              </Badge>
                              <Badge variant="secondary" className="bg-amber-100 text-amber-800" data-testid="catering-notes-count">
                                With other requirements: {withNotes}
                              </Badge>
                              <Badge variant="secondary" className="bg-slate-100 text-slate-700" data-testid="catering-unspecified-count">
                                Unspecified: {unspecified}
                              </Badge>
                            </div>
                          );
                        })()}
                        {/* Search + filter */}
                        <div className="flex flex-col sm:flex-row gap-2 mb-4">
                          <div className="relative flex-1">
                            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
                            <Input
                              placeholder="Search by name, email or phone"
                              value={attendeesSearchQ}
                              onChange={(e) => setAttendeesSearchQ(e.target.value)}
                              className="pl-8 h-9"
                              data-testid="attendees-search-input"
                            />
                          </div>
                          <Select value={attendeesFilter} onValueChange={setAttendeesFilter}>
                            <SelectTrigger className="h-9 sm:w-[200px]" data-testid="attendees-filter-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All ({attendees.length})</SelectItem>
                              <SelectItem value="checked_in">Checked in ({attendees.filter((a) => a.checked_in).length})</SelectItem>
                              <SelectItem value="not_checked_in">Not yet checked in ({attendees.filter((a) => !a.checked_in).length})</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {attendeesPaged.total === 0 ? (
                          <div className="text-center py-8 text-muted-foreground" data-testid="attendees-empty-filtered">
                            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p>No attendees match your search/filter</p>
                            <Button variant="link" className="mt-2" onClick={() => { setAttendeesSearchQ(''); setAttendeesFilter('all'); }}>
                              Clear filters
                            </Button>
                          </div>
                        ) : (
                          <div className="space-y-3" data-testid="attendees-list">
                            {attendeesPaged.items.map((attendee) => (
                          <div 
                            key={attendee.id} 
                            className="flex items-center justify-between p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="font-medium text-foreground">
                                  {attendee.name}
                                </h4>
                                {attendee.checked_in && (
                                  <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
                                    Checked In
                                  </Badge>
                                )}
                                {attendee.feedback_submitted && (
                                  <Badge variant="secondary" className="bg-violet-100 text-violet-700">
                                    Feedback
                                  </Badge>
                                )}
                                {attendee.certificate_sent && (
                                  <Badge variant="secondary" className="bg-sky-100 text-sky-700">
                                    Cert Sent
                                  </Badge>
                                )}
                                {attendee.diet_type === 'veg' && (
                                  <Badge variant="secondary" className="bg-green-100 text-green-700" data-testid={`diet-badge-${attendee.id}`}>
                                    Veg
                                  </Badge>
                                )}
                                {attendee.diet_type === 'non_veg' && (
                                  <Badge variant="secondary" className="bg-orange-100 text-orange-700" data-testid={`diet-badge-${attendee.id}`}>
                                    Non-veg
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {attendee.email}
                                {attendee.provider_number && ` • ${attendee.provider_number}`}
                              </p>
                              {attendee.diet_notes && (
                                <p className="text-xs text-amber-700 mt-1" data-testid={`diet-notes-${attendee.id}`}>
                                  🍽 {attendee.diet_notes}
                                </p>
                              )}
                              {attendee.arrival_time && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Arrived: {format(parseISO(attendee.arrival_time), 'h:mm a')}
                                  {attendee.departure_time && ` • Left: ${format(parseISO(attendee.departure_time), 'h:mm a')}`}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              {!attendee.checked_in ? (
                                <Button
                                  size="sm"
                                  onClick={() => handleCheckIn(attendee.id)}
                                  disabled={checkingIn === attendee.id}
                                  data-testid={`checkin-btn-${attendee.id}`}
                                >
                                  {checkingIn === attendee.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <CheckCircle className="w-4 h-4" />
                                  )}
                                </Button>
                              ) : !attendee.departure_time ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleCheckOut(attendee.id)}
                                  disabled={checkingIn === attendee.id}
                                  data-testid={`checkout-btn-${attendee.id}`}
                                >
                                  {checkingIn === attendee.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <CheckOut className="w-4 h-4" />
                                  )}
                                </Button>
                              ) : null}
                              
                              {/* Certificate actions - always available for checked-in attendees */}
                              {attendee.checked_in && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDownloadCertificate(attendee.id, attendee.name)}
                                    title="Download Certificate"
                                    data-testid={`download-cert-btn-${attendee.id}`}
                                  >
                                    <Download className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleSendCertificate(attendee.id)}
                                    disabled={sendingCert === attendee.id}
                                    title="Send to registered email"
                                    data-testid={`send-cert-btn-${attendee.id}`}
                                  >
                                    {sendingCert === attendee.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Mail className="w-4 h-4" />
                                    )}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openCustomEmailDialog(attendee.id, attendee.name, attendee.email)}
                                    title="Send to different email"
                                    data-testid={`send-custom-btn-${attendee.id}`}
                                  >
                                    <Send className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                          </div>
                        )}
                        {/* Pagination for attendees */}
                        {attendeesPaged.total > 0 && (
                          <PagerBar
                            total={attendeesPaged.total}
                            page={attendeesPaged.page}
                            pageSize={attendeesPaged.pageSize}
                            onPageChange={attendeesPaged.setPage}
                            testid="attendees-pager"
                          />
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
                </div>
              </TabsContent>

              <TabsContent value="sponsors" className="mt-6 space-y-6">
                {/* Public sponsor link + Prospectus PDF actions */}
                <Card className="border-border" data-testid="sponsor-public-link-card">
                  <CardHeader className="flex flex-row items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileSignature className="w-5 h-5" />
                        Sponsorship Prospectus &amp; Public EOI link
                      </CardTitle>
                      <CardDescription>
                        Share the digital prospectus with prospective sponsors. They'll see all event details, the per-event tiers, Schedule A compliance, and a built-in Expression of Interest form (with optional IIDA-member discount). Submissions appear below for your approval.
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-2"
                        onClick={async () => {
                          try {
                            await exportAPI.sponsorshipProspectusPdf(eventId, event.title);
                            toast.success('Prospectus PDF downloaded');
                          } catch (e) {
                            toast.error('Failed to generate prospectus');
                          }
                        }}
                        data-testid="download-prospectus-sponsors-btn"
                      >
                        <FileDown className="w-4 h-4" />
                        Prospectus PDF
                      </Button>
                      <Button
                        size="sm"
                        className="gap-2"
                        onClick={copyPublicSponsorLink}
                        data-testid="copy-public-sponsor-link-btn"
                      >
                        <Copy className="w-4 h-4" />
                        Copy public sponsor link
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-xs text-muted-foreground border border-dashed border-border rounded-md px-3 py-2 break-all" data-testid="public-sponsor-link-preview">
                      {window.location.origin}/sponsor/{event?.invite_code || '—'}
                    </div>
                  </CardContent>
                </Card>

                {/* EOI queue */}
                <Card className="border-border" data-testid="eoi-queue-card">
                  <CardHeader className="flex flex-row items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Building2 className="w-5 h-5" />
                        Sponsorship Expressions of Interest ({eois.length})
                      </CardTitle>
                      <CardDescription>
                        Submissions from the public prospectus link. Approve to add the sponsor to this event, then mark Payment Received to enable the tax invoice download / email.
                      </CardDescription>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-2 shrink-0"
                      onClick={() => setThankSponsorsConfirmOpen(true)}
                      disabled={eois.filter((e) => e.status === 'approved').length === 0}
                      title={eois.filter((e) => e.status === 'approved').length === 0
                        ? 'No approved sponsors on this event yet'
                        : 'Send an individualised thank-you letter + PDF from the President to every approved sponsor'}
                      data-testid="thank-sponsors-btn"
                    >
                      <Heart className="w-4 h-4" />
                      Thank all sponsors
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {eois.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No expressions of interest yet</p>
                        <p className="text-sm mt-1">Share the public sponsor link above to start collecting submissions.</p>
                      </div>
                    ) : (
                      <div className="space-y-3" data-testid="eoi-list">
                        {eois.map((eoi) => {
                          const isPaid = eoi.payment_status === 'paid';
                          const isApproved = eoi.status === 'approved';
                          const isPending = eoi.status === 'pending';
                          const isRejected = eoi.status === 'rejected';
                          return (
                            <div
                              key={eoi.id}
                              className="p-4 rounded-lg border border-border bg-muted/40"
                              data-testid={`eoi-row-${eoi.id}`}
                            >
                              <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h4 className="font-medium text-foreground">{eoi.sponsor_org}</h4>
                                    <Badge variant="secondary" className="gap-1 bg-amber-100 text-amber-800">
                                      <Award className="w-3 h-3" />
                                      {eoi.tier_name}
                                    </Badge>
                                    <Badge
                                      variant="secondary"
                                      className={
                                        isApproved ? 'bg-emerald-100 text-emerald-700' :
                                        isRejected ? 'bg-rose-100 text-rose-700' :
                                        'bg-slate-100 text-slate-700'
                                      }
                                      data-testid={`eoi-status-badge-${eoi.id}`}
                                    >
                                      {eoi.status}
                                    </Badge>
                                    {isApproved && (
                                      <Badge
                                        variant="secondary"
                                        className={isPaid ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'}
                                        data-testid={`eoi-payment-badge-${eoi.id}`}
                                      >
                                        {isPaid ? 'Paid' : 'Awaiting payment'}
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-muted-foreground mt-1">
                                    {eoi.contact_name}{eoi.contact_title ? `, ${eoi.contact_title}` : ''} · {eoi.contact_email}
                                    {eoi.contact_phone ? ` · ${eoi.contact_phone}` : ''}
                                  </p>
                                  <p className="text-sm mt-1">
                                    AUD ${Number(eoi.net_contribution_amount || 0).toLocaleString('en-AU')}
                                    {eoi.iida_member_discount_applied > 0 && (
                                      <span className="text-xs text-emerald-700 ml-1">
                                        (IIDA member discount −${Number(eoi.iida_member_discount_applied).toLocaleString('en-AU')})
                                      </span>
                                    )}
                                    {eoi.invoice_number && (
                                      <span className="text-xs text-muted-foreground font-mono ml-2">{eoi.invoice_number}</span>
                                    )}
                                  </p>
                                  {eoi.comments && (
                                    <p className="text-xs text-muted-foreground italic mt-1">"{eoi.comments}"</p>
                                  )}
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Submitted {format(parseISO(eoi.submitted_at), 'd MMM yyyy h:mm a')}
                                    {' · '}signed by <span className="italic">{eoi.signature_name}</span>
                                  </p>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  {isPending && (
                                    <>
                                      <Button
                                        size="sm"
                                        onClick={() => handleEoiApprove(eoi)}
                                        disabled={eoiActionId === eoi.id}
                                        className="gap-1"
                                        data-testid={`eoi-approve-${eoi.id}`}
                                      >
                                        {eoiActionId === eoi.id ? (
                                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        ) : (
                                          <CheckCircle className="w-3.5 h-3.5" />
                                        )}
                                        Approve
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleEoiReject(eoi)}
                                        disabled={eoiActionId === eoi.id}
                                        className="gap-1 text-destructive hover:text-destructive"
                                        data-testid={`eoi-reject-${eoi.id}`}
                                      >
                                        <X className="w-3.5 h-3.5" />
                                        Reject
                                      </Button>
                                    </>
                                  )}
                                  {isApproved && (
                                    <>
                                      <Button
                                        size="sm"
                                        variant={isPaid ? 'outline' : 'default'}
                                        onClick={() => isPaid ? handleEoiRevertPayment(eoi) : handleEoiPaymentReceived(eoi)}
                                        disabled={eoiActionId === eoi.id}
                                        className="gap-1"
                                        data-testid={`eoi-payment-toggle-${eoi.id}`}
                                      >
                                        {eoiActionId === eoi.id ? (
                                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        ) : (
                                          <CheckCircle className="w-3.5 h-3.5" />
                                        )}
                                        {isPaid ? 'Revert' : 'Payment Received'}
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleEoiDownloadInvoice(eoi)}
                                        disabled={!isApproved}
                                        title={isApproved ? 'Download tax invoice PDF' : 'Approve EOI first'}
                                        className="gap-1"
                                        data-testid={`eoi-download-invoice-${eoi.id}`}
                                      >
                                        <FileDown className="w-3.5 h-3.5" />
                                        Download invoice
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleEoiDownloadLetter(eoi)}
                                        disabled={!isApproved}
                                        title={isApproved ? 'Download formal invitation letter PDF' : 'Approve EOI first'}
                                        className="gap-1"
                                        data-testid={`eoi-download-letter-${eoi.id}`}
                                      >
                                        <FileDown className="w-3.5 h-3.5" />
                                        Download letter
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleEoiEmailInvoice(eoi)}
                                        disabled={!isApproved || emailingEoiId === eoi.id}
                                        title={isApproved ? 'Email tax invoice to sponsor' : 'Approve EOI first'}
                                        className="gap-1"
                                        data-testid={`eoi-email-invoice-${eoi.id}`}
                                      >
                                        {emailingEoiId === eoi.id ? (
                                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        ) : (
                                          <Send className="w-3.5 h-3.5" />
                                        )}
                                        Email invoice
                                      </Button>
                                    </>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleEoiDelete(eoi)}
                                    disabled={eoiActionId === eoi.id}
                                    className="text-destructive hover:text-destructive"
                                    title="Delete EOI"
                                    data-testid={`eoi-delete-${eoi.id}`}
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Existing sponsors (manual + approved EOIs) */}
                <Card className="border-border">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Building2 className="w-5 h-5" />
                      Event Sponsors
                    </CardTitle>
                    <CardDescription>
                      Sponsors associated with this event (manually added or via approved EOIs). You can also still add sponsors manually via Edit Event.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {sponsors.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No sponsors for this event yet</p>
                        <Link to={`/events/${eventId}/edit`}>
                          <Button variant="link" className="mt-2">
                            Add sponsors manually
                          </Button>
                        </Link>
                      </div>
                    ) : (
                      <div className="space-y-3" data-testid="sponsors-list">
                        {sponsors.map((sponsor) => (
                          <div 
                            key={sponsor.id} 
                            className="flex items-center justify-between p-4 rounded-lg bg-muted/50"
                          >
                            <div className="flex items-center gap-4">
                              {sponsor.logo_url ? (
                                <img 
                                  src={resolveAssetUrl(sponsor.logo_url)} 
                                  alt={sponsor.name}
                                  className="h-10 max-w-[80px] object-contain"
                                  onError={(e) => e.target.style.display = 'none'}
                                />
                              ) : (
                                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                  <Building2 className="w-5 h-5 text-primary" />
                                </div>
                              )}
                              <div>
                                <h4 className="font-medium text-foreground">{sponsor.name}</h4>
                                {sponsor.tagline && (
                                  <p className="text-sm text-muted-foreground">{sponsor.tagline}</p>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              className="gap-2"
                              onClick={() => handleDownloadSponsorCertificate(sponsor.id, sponsor.name)}
                              data-testid={`download-sponsor-cert-${sponsor.id}`}
                            >
                              <Download className="w-4 h-4" />
                              Certificate
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="invitations" className="mt-6">
                <Card className="border-border">
                  <CardHeader className="flex flex-row items-start justify-between gap-4 flex-wrap">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileSignature className="w-5 h-5" />
                        Special Guest Invitations
                      </CardTitle>
                      <CardDescription>
                        Send formal invitation letters to Chief Guests, Guests of Honor, and other distinguished invitees.
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Select value={invitationStatusFilter} onValueChange={setInvitationStatusFilter}>
                        <SelectTrigger className="h-9 w-[180px]" data-testid="invitation-status-filter">
                          <SelectValue placeholder="All statuses" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All ({invitations.length})</SelectItem>
                          <SelectItem value="draft">Draft ({invitations.filter(i => i.status === 'draft').length})</SelectItem>
                          <SelectItem value="sent">Sent ({invitations.filter(i => i.status === 'sent').length})</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        className="gap-2"
                        onClick={() => openInvitationDialog('create')}
                        data-testid="create-invitation-btn"
                      >
                        <FileSignature className="w-4 h-4" />
                        New Guest Invitation
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {invitations.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <FileSignature className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No guest invitations yet</p>
                        <p className="text-sm mt-1">Click "New Guest Invitation" to draft one</p>
                      </div>
                    ) : (() => {
                      const visibleInvitations = invitations.filter((i) =>
                        invitationStatusFilter === 'all' ? true : i.status === invitationStatusFilter
                      );
                      if (visibleInvitations.length === 0) {
                        return (
                          <div className="text-center py-8 text-muted-foreground" data-testid="invitations-empty-filtered">
                            <FileSignature className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p>No invitations with status "{invitationStatusFilter}"</p>
                            <Button
                              variant="link"
                              className="mt-2"
                              onClick={() => setInvitationStatusFilter('all')}
                            >
                              Show all
                            </Button>
                          </div>
                        );
                      }
                      return (
                      <div className="space-y-3" data-testid="invitations-list">
                        {visibleInvitations.map((inv) => {
                          const displayName = inv.guest_title
                            ? `${inv.guest_title} ${inv.guest_name}`
                            : inv.guest_name;
                          return (
                            <div
                              key={inv.id}
                              className="flex items-start justify-between gap-3 p-4 rounded-lg bg-muted/50"
                              data-testid={`invitation-row-${inv.id}`}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h4 className="font-medium text-foreground">{displayName}</h4>
                                  <Badge variant="secondary" className="gap-1 bg-amber-100 text-amber-800">
                                    <Award className="w-3 h-3" />
                                    {inv.role}
                                  </Badge>
                                  <Badge
                                    variant="secondary"
                                    className={
                                      inv.status === 'sent' ? 'bg-sky-100 text-sky-700' :
                                      'bg-slate-100 text-slate-700'
                                    }
                                  >
                                    {inv.status}
                                  </Badge>
                                </div>
                                {inv.guest_email && (
                                  <p className="text-sm text-muted-foreground mt-1">{inv.guest_email}</p>
                                )}
                                <p className="text-xs text-muted-foreground mt-1">
                                  Updated {format(parseISO(inv.updated_at || inv.created_at), 'MMM d, yyyy h:mm a')}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Select
                                  value={inv.status}
                                  onValueChange={(v) => setInvitationStatus(inv, v)}
                                >
                                  <SelectTrigger className="h-8 w-[100px]" data-testid={`invitation-status-${inv.id}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="draft">Draft</SelectItem>
                                    <SelectItem value="sent">Sent</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openInvitationDialog('edit', inv)}
                                  title="Edit"
                                  data-testid={`edit-invitation-${inv.id}`}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDownloadInvitation(inv)}
                                  disabled={downloadingInvitation === inv.id}
                                  title="Download PDF"
                                  data-testid={`download-invitation-${inv.id}`}
                                >
                                  {downloadingInvitation === inv.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Download className="w-4 h-4" />
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openSendInvitationDialog(inv)}
                                  disabled={sendingInvitation === inv.id}
                                  title={inv.status === 'sent' ? 'Resend invitation' : 'Email to guest'}
                                  data-testid={`send-invitation-${inv.id}`}
                                >
                                  {sendingInvitation === inv.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Send className="w-4 h-4" />
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDeleteInvitation(inv)}
                                  className="text-destructive hover:text-destructive"
                                  title="Delete"
                                  data-testid={`delete-invitation-${inv.id}`}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="feedback" className="mt-6 space-y-6">
                {/* Public anonymous feedback link card */}
                <Card className="border-border" data-testid="feedback-link-card">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Anonymous Feedback Link
                    </CardTitle>
                    <CardDescription>
                      Share this link to collect anonymous feedback after the event. Send it to all
                      checked-in attendees with one click, or copy &amp; paste it into a group chat / WhatsApp.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div
                      className="text-xs text-muted-foreground border border-dashed border-border rounded-md px-3 py-2 break-all font-mono"
                      data-testid="feedback-public-link-preview"
                    >
                      {feedbackPublicLink || '—'}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-2"
                        onClick={copyFeedbackLink}
                        data-testid="copy-feedback-link-btn"
                      >
                        <Copy className="w-4 h-4" />
                        Copy link
                      </Button>
                      <Button
                        size="sm"
                        className="gap-2"
                        onClick={() => { setFeedbackBulkCover(''); setFeedbackBulkResult(null); setFeedbackBulkDialog(true); }}
                        disabled={attendees.filter((a) => a.checked_in).length === 0}
                        data-testid="bulk-email-feedback-btn"
                      >
                        <Send className="w-4 h-4" />
                        Email to checked-in attendees ({attendees.filter((a) => a.checked_in).length})
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Feedback responses summary */}
                <Card className="border-border">
                  <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
                    <div>
                      <CardTitle className="text-lg">Feedback Responses</CardTitle>
                      <CardDescription>
                        {attendees.filter(a => a.feedback_submitted).length} attributed responses ·
                        anonymous submissions counted separately
                      </CardDescription>
                    </div>
                    {attendees.filter(a => a.feedback_submitted).length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={async () => {
                          try {
                            await exportAPI.feedbackCsv(eventId, event.title);
                            toast.success('Feedback exported');
                          } catch (e) {
                            toast.error('Failed to export feedback');
                          }
                        }}
                        data-testid="export-feedback-csv-btn"
                      >
                        <FileSpreadsheet className="w-4 h-4" />
                        Export CSV
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    {attendees.filter(a => a.feedback_submitted).length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No attributed feedback yet</p>
                        <p className="text-sm mt-1">Anonymous responses still appear in the Analytics page summary.</p>
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground">
                        <p>{attendees.filter(a => a.feedback_submitted).length} attendees have submitted feedback.</p>
                        <p className="mt-2">View detailed feedback summary in the Analytics page.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="audit" className="mt-6">
                <Card className="border-border">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <History className="w-5 h-5" />
                      Audit Trail
                    </CardTitle>
                    <CardDescription>
                      Complete history of event activities for RACGP compliance
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {auditLogs.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No audit logs yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3" data-testid="audit-logs">
                        {auditLogs.map((log) => (
                          <div key={log.id} className="flex gap-4 p-3 bg-muted/50 rounded-lg text-sm">
                            <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-primary" />
                            <div className="flex-1">
                              <p className="font-medium text-foreground capitalize">
                                {log.action.replace(/_/g, ' ')}
                              </p>
                              <p className="text-muted-foreground">{log.details}</p>
                              <div className="flex items-center gap-2 flex-wrap mt-1">
                                <p className="text-xs text-muted-foreground">
                                  {format(parseISO(log.timestamp), 'MMM d, yyyy h:mm a')}
                                </p>
                                {(log.actor_name || log.actor_email) ? (
                                  <Badge variant="secondary" className="text-xs h-5" data-testid={`audit-actor-${log.id}`}>
                                    by {log.actor_name || log.actor_email}
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-xs h-5 text-muted-foreground" data-testid={`audit-actor-${log.id}`}>
                                    public action
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* QR Code */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <QrCode className="w-5 h-5" />
                  Registration QR Code
                </CardTitle>
                <CardDescription>
                  Scan to register for this event
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col items-center">
                <div className="qr-container mb-4" data-testid="event-qr-code">
                  <QRCode value={registrationUrl} size={180} />
                </div>
                <p className="text-xs text-muted-foreground text-center mb-4 break-all">
                  {registrationUrl}
                </p>
                <div className="flex gap-2 w-full">
                  <Button 
                    variant="outline" 
                    className="flex-1 gap-2"
                    onClick={copyInviteLink}
                    data-testid="copy-invite-link-btn"
                  >
                    <Copy className="w-4 h-4" />
                    Copy Link
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => window.open(registrationUrl, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Event Stats */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg">Event Stats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Registered</span>
                  <span className="font-medium">{attendees.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Checked In</span>
                  <span className="font-medium">{attendees.filter(a => a.checked_in).length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Feedback Submitted</span>
                  <span className="font-medium">{attendees.filter(a => a.feedback_submitted).length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Certificates Sent</span>
                  <span className="font-medium">{attendees.filter(a => a.certificate_sent).length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Certificates Downloaded</span>
                  <span className="font-medium">{attendees.filter(a => a.certificate_downloaded).length}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Custom Email Dialog */}
      <Dialog open={customEmailDialog.open} onOpenChange={(open) => !open && setCustomEmailDialog({ open: false, attendeeId: null, attendeeName: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Certificate to Different Email</DialogTitle>
            <DialogDescription>
              Send certificate for {customEmailDialog.attendeeName} to a different email address.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="custom-email">Email Address</Label>
              <Input
                id="custom-email"
                type="email"
                placeholder="doctor@example.com"
                value={customEmail}
                onChange={(e) => setCustomEmail(e.target.value)}
                data-testid="custom-email-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCustomEmailDialog({ open: false, attendeeId: null, attendeeName: '' })}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendCustomEmail}
              disabled={sendingCustom}
              data-testid="send-custom-email-btn"
            >
              {sendingCustom ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Certificate
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sponsorship Invitation Dialog */}
      <Dialog
        open={invitationDialog.open}
        onOpenChange={(open) => !open && setInvitationDialog({ open: false, mode: 'create', invitation: null })}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {invitationDialog.mode === 'create' ? 'New Guest Invitation' : 'Edit Guest Invitation'}
            </DialogTitle>
            <DialogDescription>
              Formal invitation letter for a Chief Guest, Guest of Honor, or other distinguished invitee. The PDF includes IIDA letterhead and the body shown below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid sm:grid-cols-[140px_1fr] gap-3">
              <div className="space-y-2">
                <Label htmlFor="inv-title">Honorific</Label>
                <Input
                  id="inv-title"
                  placeholder="Dr / Prof / Hon"
                  value={invitationForm.guest_title}
                  onChange={(e) => setInvitationForm((p) => ({ ...p, guest_title: e.target.value }))}
                  data-testid="invitation-guest-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="inv-guest-name">Guest name *</Label>
                <Input
                  id="inv-guest-name"
                  placeholder="Full name of the guest"
                  value={invitationForm.guest_name}
                  onChange={(e) => setInvitationForm((p) => ({ ...p, guest_name: e.target.value }))}
                  data-testid="invitation-guest-name-input"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="inv-role">Role *</Label>
                <Select
                  value={
                    ['Chief Guest', 'Guest of Honor', 'Special Guest'].includes(invitationForm.role)
                      ? invitationForm.role
                      : 'custom'
                  }
                  onValueChange={(v) => {
                    if (v === 'custom') {
                      setInvitationForm((p) => ({ ...p, role: p.role && !['Chief Guest','Guest of Honor','Special Guest'].includes(p.role) ? p.role : '' }));
                    } else {
                      setInvitationForm((p) => ({ ...p, role: v }));
                    }
                  }}
                >
                  <SelectTrigger data-testid="invitation-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Chief Guest">Chief Guest</SelectItem>
                    <SelectItem value="Guest of Honor">Guest of Honor</SelectItem>
                    <SelectItem value="Special Guest">Special Guest</SelectItem>
                    <SelectItem value="custom">Custom…</SelectItem>
                  </SelectContent>
                </Select>
                {!['Chief Guest', 'Guest of Honor', 'Special Guest'].includes(invitationForm.role) && (
                  <Input
                    placeholder="Custom role (e.g. Keynote Speaker)"
                    value={invitationForm.role}
                    onChange={(e) => setInvitationForm((p) => ({ ...p, role: e.target.value }))}
                    data-testid="invitation-role-custom-input"
                  />
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="inv-guest-email">Email (for sending invitation)</Label>
                <Input
                  id="inv-guest-email"
                  type="email"
                  placeholder="guest@example.com"
                  value={invitationForm.guest_email}
                  onChange={(e) => setInvitationForm((p) => ({ ...p, guest_email: e.target.value }))}
                  data-testid="invitation-guest-email-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="inv-message">Custom message (optional)</Label>
              <p className="text-xs text-muted-foreground">
                Leave blank to use the standard warm-and-formal IIDA invitation template. Anything typed here completely replaces the default body.
              </p>
              <Textarea
                id="inv-message"
                rows={8}
                placeholder="(Leave blank for the default invitation, or paste your custom letter here)"
                value={invitationForm.custom_message}
                onChange={(e) => setInvitationForm((p) => ({ ...p, custom_message: e.target.value }))}
                data-testid="invitation-message-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setInvitationDialog({ open: false, mode: 'create', invitation: null })}
            >
              Cancel
            </Button>
            <Button
              onClick={saveInvitation}
              disabled={savingInvitation}
              className="gap-2"
              data-testid="save-invitation-btn"
            >
              {savingInvitation ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Special Guest Invitation Email Dialog */}
      <Dialog
        open={sendInvitationDialog.open}
        onOpenChange={(open) => !open && setSendInvitationDialog({ open: false, invitation: null })}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Email Guest Invitation</DialogTitle>
            <DialogDescription>
              Sends the invitation PDF plus an optional cover note to the guest. The invitation will be marked as <strong>sent</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="send-email">Recipient email</Label>
              <Input
                id="send-email"
                type="email"
                placeholder="guest@example.com"
                value={sendForm.email}
                onChange={(e) => setSendForm((p) => ({ ...p, email: e.target.value }))}
                data-testid="send-invitation-email-input"
              />
              <p className="text-xs text-muted-foreground">
                Defaults to the email on file. Resend requires a verified sending domain — see Branding settings for status.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cover-msg">Cover message (optional)</Label>
              <Textarea
                id="cover-msg"
                rows={4}
                placeholder="A short personal note that appears above the invitation in the email body."
                value={sendForm.cover_message}
                onChange={(e) => setSendForm((p) => ({ ...p, cover_message: e.target.value }))}
                data-testid="send-invitation-cover-input"
              />
            </div>
            {sendError && (
              <div
                className="p-3 rounded-md border border-rose-300 bg-rose-50 text-sm text-rose-800"
                data-testid="send-invitation-error"
              >
                <strong>Send failed:</strong>
                <p className="mt-1 whitespace-pre-line">{sendError}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSendInvitationDialog({ open: false, invitation: null })}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendInvitation}
              disabled={sendingInvitation !== null}
              className="gap-2"
              data-testid="send-invitation-confirm-btn"
            >
              {sendingInvitation !== null ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Send invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete event confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => !deletingEvent && setDeleteDialogOpen(open)}>
        <AlertDialogContent data-testid="delete-event-detail-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Delete this event?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">You are about to permanently delete:</span>
              <span className="block font-medium text-foreground">{event?.title}</span>
              <span className="block">
                This will also remove <strong>{attendees.length} attendee(s)</strong>,
                all <strong>feedback responses</strong>, and any <strong>sponsorship invitations</strong>
                {' '}tied to this event. Audit logs are kept for compliance and will record this deletion against your admin account.
              </span>
              <span className="block text-destructive font-medium">This action cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingEvent} data-testid="delete-event-detail-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async (e) => {
                e.preventDefault();
                setDeletingEvent(true);
                try {
                  const res = await eventAPI.delete(eventId);
                  const removed = res?.data?.removed_attendees ?? 0;
                  const fb = res?.data?.removed_feedback ?? 0;
                  toast.success(
                    `Event deleted${removed || fb ? ` (also removed ${removed} attendee(s) & ${fb} feedback record(s))` : ''}`
                  );
                  navigate('/events');
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to delete event');
                  setDeletingEvent(false);
                }
              }}
              disabled={deletingEvent}
              data-testid="delete-event-detail-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingEvent ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" />Delete event</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk reminder email confirmation */}
      <AlertDialog open={reminderConfirmOpen} onOpenChange={(open) => !sendingReminders && setReminderConfirmOpen(open)}>
        <AlertDialogContent data-testid="send-reminders-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Send reminder to all attendees?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                This will email a reminder + personal check-in QR code to <strong>{attendees.length} registered attendee(s)</strong> of:
              </span>
              <span className="block font-medium text-foreground">{event?.title}</span>
              <span className="block text-sm">
                The email wording adapts automatically based on the event date (past / today / future).
                Every attendee is re-emailed regardless of whether they received a prior reminder.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sendingReminders} data-testid="send-reminders-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async (e) => {
                e.preventDefault();
                setSendingReminders(true);
                const t = toast.loading(`Sending reminders to ${attendees.length} attendee(s)…`);
                try {
                  const res = await eventAPI.sendReminders(eventId);
                  const { sent, failed, attempted, context, failures } = res.data;
                  if (failed === 0) {
                    toast.success(`Reminder sent to all ${sent}/${attempted} attendee(s)`, { id: t });
                  } else {
                    const firstErr = failures?.[0]?.error?.slice(0, 80) || 'unknown error';
                    toast.warning(
                      `Sent to ${sent}/${attempted}. ${failed} failed — first error: ${firstErr}`,
                      { id: t, duration: 8000 },
                    );
                  }
                  setReminderConfirmOpen(false);
                  fetchData(); // refresh so reminder_sent_at updates are picked up
                  // Log context to console for admin awareness
                  console.log(`[reminder] context=${context} attempted=${attempted} sent=${sent} failed=${failed}`);
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to send reminders', { id: t });
                } finally {
                  setSendingReminders(false);
                }
              }}
              disabled={sendingReminders || attendees.length === 0}
              data-testid="send-reminders-confirm"
            >
              {sendingReminders ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>
              ) : (
                <><Mail className="w-4 h-4 mr-2" />Send to all</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Thank all sponsors — confirmation dialog */}
      <AlertDialog
        open={thankSponsorsConfirmOpen}
        onOpenChange={(open) => {
          if (thankSponsorsSending) return;
          setThankSponsorsConfirmOpen(open);
          if (!open) setThankSponsorsResult(null);
        }}
      >
        <AlertDialogContent data-testid="thank-sponsors-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5" />
              Send thank-you letter to all sponsors?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                This will email an individualised thank-you letter (HTML + PDF attachment) signed by the President to every <strong>approved sponsor</strong> of:
              </span>
              <span className="block font-medium text-foreground">{event?.title}</span>
              <span className="block text-sm">
                Currently <strong>{eois.filter((e) => e.status === 'approved').length}</strong> approved sponsor(s) will receive the email at the address they registered with. Pending or rejected EOIs are skipped.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          {thankSponsorsResult && (
            <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs space-y-1" data-testid="thank-sponsors-result">
              <div>
                Attempted <strong>{thankSponsorsResult.attempted}</strong> · Sent <strong>{thankSponsorsResult.sent}</strong> · Skipped <strong>{thankSponsorsResult.skipped}</strong> · Failed <strong>{thankSponsorsResult.failed}</strong>
              </div>
              {thankSponsorsResult.failures?.length > 0 && (
                <ul className="mt-1 pl-4 list-disc text-rose-700">
                  {thankSponsorsResult.failures.slice(0, 5).map((f, i) => (
                    <li key={i}>
                      <span className="font-medium">{f.sponsor_org || f.email}</span>: {f.error}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={thankSponsorsSending} data-testid="thank-sponsors-cancel">
              {thankSponsorsResult ? 'Close' : 'Cancel'}
            </AlertDialogCancel>
            {!thankSponsorsResult && (
              <AlertDialogAction
                onClick={async (e) => {
                  e.preventDefault();
                  await handleThankAllSponsors();
                }}
                disabled={thankSponsorsSending || eois.filter((x) => x.status === 'approved').length === 0}
                data-testid="thank-sponsors-confirm"
              >
                {thankSponsorsSending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>
                ) : (
                  <><Heart className="w-4 h-4 mr-2" />Send to all</>
                )}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Push certificates — confirmation dialog */}
      <AlertDialog
        open={pushCertsConfirmOpen}
        onOpenChange={(open) => {
          if (pushingCerts) return;
          setPushCertsConfirmOpen(open);
          if (!open) setPushCertsResult(null);
        }}
      >
        <AlertDialogContent data-testid="push-certs-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5" />
              Push certificates + feedback link?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                This will email each <strong>checked-in attendee</strong> their CPD certificate (PDF attached) together with a link to the feedback form. Uses their registered email.
              </span>
              <span className="block font-medium text-foreground">{event?.title}</span>
              <span className="block text-sm">
                <strong>{attendees.filter((a) => a.checked_in && !a.post_event_certificate_sent_at).length}</strong> attendee(s) will receive it now — anyone who already got one is skipped automatically (idempotent). Late check-ins are picked up on the next 15-min scheduler tick.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pushCertsResult && (
            <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs space-y-1" data-testid="push-certs-result">
              <div>
                Attempted <strong>{pushCertsResult.attempted}</strong> · Sent <strong>{pushCertsResult.sent}</strong> · Failed <strong>{pushCertsResult.failed}</strong>
              </div>
              {pushCertsResult.failures?.length > 0 && (
                <ul className="mt-1 pl-4 list-disc text-rose-700">
                  {pushCertsResult.failures.slice(0, 5).map((f, i) => (
                    <li key={i}>
                      <span className="font-medium">{f.name || f.email}</span>: {f.error}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pushingCerts} data-testid="push-certs-cancel">
              {pushCertsResult ? 'Close' : 'Cancel'}
            </AlertDialogCancel>
            {!pushCertsResult && (
              <AlertDialogAction
                onClick={async (e) => {
                  e.preventDefault();
                  setPushingCerts(true);
                  setPushCertsResult(null);
                  const t = toast.loading('Pushing certificates + feedback link…');
                  try {
                    const res = await eventAPI.pushCertificates(eventId);
                    setPushCertsResult(res.data);
                    const { attempted, sent, failed } = res.data;
                    if (attempted === 0) {
                      toast.info('All checked-in attendees already have their certificates', { id: t });
                    } else if (failed === 0) {
                      toast.success(`Certificate + feedback link sent to ${sent}/${attempted}`, { id: t });
                    } else {
                      toast.warning(`Sent ${sent}/${attempted}. ${failed} failed — see details.`, { id: t, duration: 8000 });
                    }
                    if (!failed && attempted > 0) setPushCertsConfirmOpen(false);
                    fetchData();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Push failed', { id: t });
                  } finally {
                    setPushingCerts(false);
                  }
                }}
                disabled={pushingCerts || attendees.filter((a) => a.checked_in).length === 0}
                data-testid="push-certs-confirm"
              >
                {pushingCerts ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Pushing…</>
                ) : (
                  <><Award className="w-4 h-4 mr-2" />Push now</>
                )}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog open={feedbackBulkDialog} onOpenChange={setFeedbackBulkDialog}>
        <DialogContent className="max-w-lg" data-testid="bulk-feedback-dialog">
          <DialogHeader>
            <DialogTitle>Email feedback link</DialogTitle>
            <DialogDescription>
              Sends the anonymous feedback link to every <strong>checked-in</strong> attendee
              ({attendees.filter((a) => a.checked_in).length} recipient{attendees.filter((a) => a.checked_in).length === 1 ? '' : 's'}).
              Each email includes the event title, date, and an optional cover note.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="fb-cover">Cover note (optional)</Label>
              <Textarea
                id="fb-cover"
                rows={4}
                placeholder="e.g. Thank you for attending tonight — your feedback shapes our next event."
                value={feedbackBulkCover}
                onChange={(e) => setFeedbackBulkCover(e.target.value)}
                data-testid="bulk-feedback-cover-input"
              />
            </div>
            {feedbackBulkResult && (
              <div
                className={`p-3 rounded-md border text-sm ${
                  feedbackBulkResult.sent === feedbackBulkResult.total_recipients
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                    : 'border-amber-300 bg-amber-50 text-amber-800'
                }`}
                data-testid="bulk-feedback-result"
              >
                <p><strong>Sent:</strong> {feedbackBulkResult.sent} of {feedbackBulkResult.total_recipients}</p>
                {(feedbackBulkResult.failed || []).length > 0 && (
                  <ul className="mt-1 text-xs list-disc pl-4 space-y-0.5">
                    {feedbackBulkResult.failed.map((f, i) => (
                      <li key={i}><strong>{f.email}</strong>: {f.error}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setFeedbackBulkDialog(false)}
              disabled={feedbackBulkSending}
            >
              Close
            </Button>
            <Button
              onClick={handleBulkEmailFeedback}
              disabled={feedbackBulkSending || attendees.filter((a) => a.checked_in).length === 0}
              className="gap-2"
              data-testid="bulk-feedback-send-btn"
            >
              {feedbackBulkSending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Send to {attendees.filter((a) => a.checked_in).length} checked-in
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
